import {
  type ApiContext,
  ScanNotFound,
  ScanStartRefused,
  StreamRefused,
  ValidationError,
} from "@hobbyshopify/backend-client";
import { hasError } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import { createAuthHandler } from "./core/auth/auth-handler";
import type { MerchantSession } from "./core/auth/merchant-session";
import type { BackendApi } from "./core/ops/api";
import { readReviewQueueAgain } from "./core/ops/review-ops";
import { loadScan, streamScan } from "./core/ops/scan-ops";
import { errorText } from "./core/primitives/errors";
import { ShareLinks } from "./core/primitives/share-links";
import { CapabilitiesRepository } from "./core/repositories/capabilities-repository";
import { CountriesRepository } from "./core/repositories/countries-repository";
import { CustomersRepository } from "./core/repositories/customers-repository";
import { LocationsRepository } from "./core/repositories/locations-repository";
import { PendingCartsRepository } from "./core/repositories/pending-carts-repository";
import { ProfileRepository } from "./core/repositories/profile-repository";
import { ProvidersRepository } from "./core/repositories/providers-repository";
import { ReviewQueueRepository } from "./core/repositories/review-queue-repository";
import { StockListRepository } from "./core/repositories/stock-list-repository";
import { StoreRepository } from "./core/repositories/store-repository";
import { BroadcastStudio } from "./core/services/broadcast-studio";
import { blobToDataUrl } from "./core/services/scan-image";
import { ScanRefused, ScanSessions } from "./core/services/scan-session";
import { StripePayments } from "./core/services/stripe-payments";
import { StripeTerminal } from "./core/services/stripe-terminal";

/**
 * Everything the app is made of, constructed once at startup. Pages declare the
 * subset they need with `Pick<AppDeps, ...>` and receive it through `pick()`.
 */
export interface AppDeps {
  readonly client: BackendApi;
  /** Ops go through `authHandler`; the sign-in ops and the streams read the session directly. */
  readonly session: MerchantSession;
  readonly authHandler: AuthHandler<ApiContext>;
  readonly profileRepository: ProfileRepository;
  readonly storeRepository: StoreRepository;
  readonly locationsRepository: LocationsRepository;
  readonly countriesRepository: CountriesRepository;
  readonly pendingCartsRepository: PendingCartsRepository;
  readonly providersRepository: ProvidersRepository;
  readonly capabilitiesRepository: CapabilitiesRepository;
  readonly reviewQueueRepository: ReviewQueueRepository;
  readonly customersRepository: CustomersRepository;
  readonly stockListRepository: StockListRepository;
  /** The scanner's sessions: the counter's, and each counter customer's; reset at sign-out. */
  readonly scanSessions: ScanSessions;
  readonly broadcastStudio: BroadcastStudio;
  readonly stripeTerminal: StripeTerminal;
  readonly stripePayments: StripePayments;
  /** Where the public site shows a shop, stream, break or show: what a share button hands on. */
  readonly shareLinks: ShareLinks;
}

export function createAppDeps(o: {
  client: BackendApi;
  session: MerchantSession;
  /** Object URLs for the scanner's thumbnails; tests run without a DOM. */
  previewUrl?: (picture: Blob) => string;
  revokePreview?: (url: string) => void;
  /** The platform's Stripe publishable key, for paying in the app; none in tests. */
  stripePublishableKey?: string | null;
  /** The public site's origin (`VITE_HOBBYSHOP_URL`); hobbyshop.ai when unset. */
  publicSiteUrl?: string | null;
}): AppDeps {
  const { client, session } = o;
  const authHandler = createAuthHandler(session);
  const storeRepository = new StoreRepository();
  const reviewQueueRepository = new ReviewQueueRepository();
  const customersRepository = new CustomersRepository();

  const scanSessions = new ScanSessions({
    run: async ({ shopId, locationId, customerId }, images, onProgress, signal) => {
      const imageUrl = images.front ? await blobToDataUrl(images.front) : undefined;
      const backImageUrl = images.back ? await blobToDataUrl(images.back) : undefined;
      try {
        return await streamScan({
          client,
          session,
          shopId,
          locationId,
          images: {
            ...(imageUrl ? { imageUrl } : {}),
            attemptId: images.attemptId,
            ...(images.codes.length > 0 ? { codes: [...images.codes] } : {}),
            ...(images.kind ? { kind: images.kind } : {}),
            ...(backImageUrl ? { backImageUrl } : {}),
            ...(customerId ? { customerId } : {}),
          },
          onProgress,
          signal,
        });
      } catch (e) {
        // Refused before it began: the card says why, in the server's words.
        const said =
          e instanceof ScanStartRefused
            ? e.reason
            : e instanceof StreamRefused
              ? e.errors.map((r) => (r instanceof ValidationError ? r.detailedDesc : "")).find(Boolean)
              : undefined;
        throw said ? new ScanRefused(said) : e;
      }
    },
    read: async ({ shopId }, scanId) => {
      const r = await loadScan({ client, authHandler, shopId, scanId });
      if (r.ok) return r.value;
      if (hasError(r, ScanNotFound)) return "gone";
      throw new Error(errorText(r.errors));
    },
    // A landed scan joins a group, and which group is the server's call, so the lists are read again: the counter's
    // queue here, so its card is on Review by the time it turns ready, as iOS's list holds it at Done. A customer's
    // scans are read by their page.
    landed: (scope) => {
      customersRepository.markStale();
      const { shopId, locationId } = storeRepository;
      if (scope.customerId !== null || scope.shopId !== shopId || scope.locationId !== locationId) {
        reviewQueueRepository.markStale();
        return;
      }
      return readReviewQueueAgain({ client, authHandler, shopId, locationId, reviewQueueRepository, storeRepository });
    },
    failed: () => {
      reviewQueueRepository.markStale();
      customersRepository.markStale();
    },
    ...(o.previewUrl ? { previewUrl: o.previewUrl } : {}),
    ...(o.revokePreview ? { revokePreview: o.revokePreview } : {}),
  });

  return {
    client,
    session,
    authHandler,
    profileRepository: new ProfileRepository(),
    storeRepository,
    locationsRepository: new LocationsRepository(),
    countriesRepository: new CountriesRepository(),
    pendingCartsRepository: new PendingCartsRepository(),
    providersRepository: new ProvidersRepository(),
    capabilitiesRepository: new CapabilitiesRepository(),
    reviewQueueRepository,
    customersRepository,
    stockListRepository: new StockListRepository(),
    scanSessions,
    broadcastStudio: new BroadcastStudio({ client, session }),
    stripeTerminal: new StripeTerminal({ client, session }),
    stripePayments: new StripePayments(o.stripePublishableKey ?? null),
    shareLinks: new ShareLinks(o.publicSiteUrl),
  };
}
